import React from "react";
import { get, identity, startCase, toString } from "lodash";
import { PanelPropertiesCard } from "@sonder/ui/source/styled/components/PanelPropertiesCard";
import { RouterLink } from "@sonder/ui/source/styled/fragments/RouterLink";
import { useStore as useAuthStore } from "auth/store";
import { ONE_MINUTE } from "core/constants";
import { TaskCufSpace, TaskData } from "core/queries/GetTask";
import { humanizeTime } from "core/utils/humanizeTime";
import { LinkEnum } from "tasks/constants";
import {
  MetadataLink,
  formatDate,
  getTaskOptionByType,
  parseGraphQLId,
} from "tasks/helpers";

const formatBoolean = (value: Boolean) => (value ? "Yes" : "No");

export const METADATA_INFO = {
  heading: "Metadata",
  options: [
    {
      label: "Creator",
      path: "author.name",
      transform: identity,
    },
    {
      label: "Task type",
      path: "type",
      transform: (type: string) =>
        get(getTaskOptionByType(type), ["displayName"], type),
    },
    {
      label: "Location",
      path: "taskContext.taskLocation.name",
      transform: startCase,
    },
    {
      label: "Category",
      path: "taskContext.taskCategory.name",
      transform: startCase,
    },
    {
      label: "Context",
      path: "taskContext.name",
      transform: startCase,
    },
    {
      label: "Earliest Start Time",
      path: "earliestStartAt",
      transform: formatDate,
    },
    // TODO: This is legacy and will be deprecated in favor of earliestStartAt on Task itself
    {
      label: "Earliest Start Time",
      path: "structuredMetadata.earliestStartTime",
      transform: identity,
      shouldShow: (value: string, data: TaskData) => {
        return !data.earliestStartAt;
      },
    },
    {
      label: "Latest End Time",
      path: "latestEndAt",
      transform: formatDate,
    },
    {
      label: "Estimated duration",
      path: "cufSpace",
      transform: (value: TaskCufSpace, data: TaskData) => {
        const duration = data.estimatedDuration || value.defaultCleaningMinutes;
        const humanizedDuration = humanizeTime(duration * ONE_MINUTE);
        return humanizedDuration;
      },
    },
    {
      label: "Scheduled Start Time",
      path: "scheduledStartAt",
      transform: formatDate,
    },
    {
      label: "Space Type",
      path: "cufSpace.type",
      transform: startCase,
    },
    {
      label: "Reason for Ejection",
      path: "structuredMetadata.reasonForEviction",
      transform: startCase,
    },
    {
      label: "Reason for Ejection",
      path: "structuredMetadata.reasonForEjection",
      transform: startCase,
    },
    {
      label: "Ejection At",
      path: "structuredMetadata.evictionAt",
      transform: formatDate,
    },
    {
      label: "Guest already Aware",
      path: "structuredMetadata.guestAlreadyAware",
      transform: formatBoolean,
    },
    {
      label: "Received Previous Warning",
      path: "structuredMetadata.guestReceivedPreviousWarning",
      transform: formatBoolean,
    },
    {
      label: "Security Onsite",
      path: "structuredMetadata.securityOnsite",
      transform: formatBoolean,
    },
    {
      label: "Building link",
      path: "cufSpace.building.id",
      transform: (value: string, data: TaskData) => (
        <MetadataLink
          href={`${ADMIN_HOST}/buildings/${parseGraphQLId(value)}`}
          segmentElement="building_link"
          task={data}
        />
      ),
      shouldShow: (value: string) => {
        const {
          state: { user },
        } = useAuthStore();
        return (value && (user?.isTOMMember || user?.isGETMember)) || false;
      },
    },
    {
      label: "Asana link",
      path: "asanaUrl",
      transform: (value: string, data: TaskData) => (
        <MetadataLink href={value} segmentElement="asana_link" task={data} />
      ),
      shouldShow: (value: string, data: TaskData) => {
        const linkOptions = getTaskOptionByType(data.type, false);
        return linkOptions && linkOptions.allowedLinks.includes(LinkEnum.asana);
      },
    },
    {
      label: "Time of move",
      path: "structuredMetadata.intraStayTime",
      transform: toString,
    },
    {
      label: "OTA",
      path: "structuredMetadata.ota",
      transform: toString,
    },
    {
      label: "Reason",
      path: "structuredMetadata.reasonForRelocation",
      transform: startCase,
    },
    {
      label: "Target unit 1",
      path: "structuredMetadata.preferredRelocationUnit.id",
      transform: (value: string, data: TaskData) => (
        <MetadataLink
          href={`${ADMIN_HOST}/units/${parseGraphQLId(value)}`}
          segmentElement="preferred_relocation_link"
          task={data}
        />
      ),
    },
    {
      label: "Target unit 2",
      path: "structuredMetadata.fallbackRelocationUnit.id",
      transform: (value: string, data: TaskData) => (
        <MetadataLink
          href={`${ADMIN_HOST}/units/${parseGraphQLId(value)}`}
          segmentElement="fallback_relocation_link"
          task={data}
        />
      ),
    },
    {
      label: "Google Form",
      path: "structuredMetadata.googleFormUrl",
      transform: (value: string, data: TaskData) => (
        <MetadataLink
          href={value}
          segmentElement="google_form_link"
          task={data}
        />
      ),
    },
    {
      label: "Drop-off location",
      path: "structuredMetadata.dropOffLocation",
      transform: startCase,
    },
    {
      label: "Drop-off time",
      path: "structuredMetadata.dropOffTime",
      transform: identity,
    },
    {
      label: "Pick-up time",
      path: "structuredMetadata.pickUpTime",
      transform: identity,
    },
    {
      label: "Pick-up quantity",
      path: "structuredMetadata.pickUpQuantity",
      transform: identity,
    },
    {
      label: "Pick-up Task",
      path: "structuredMetadata.pickUpTask.id",
      transform: (id: string) => (
        <RouterLink to={`/tasks/${parseGraphQLId(id)}`}>View Task</RouterLink>
      ),
    },
    {
      label: "Delivery Task",
      path: "structuredMetadata.deliveryTask.id",
      transform: (id: string) => (
        <RouterLink to={`/tasks/${parseGraphQLId(id)}`}>View Task</RouterLink>
      ),
    },
    {
      label: "Previous Task",
      path: "previousRecurrenceTask.id",
      transform: (id: string) => (
        <RouterLink to={`/tasks/${parseGraphQLId(id)}`}>View Task</RouterLink>
      ),
    },
    {
      label: "Subsequent Task",
      path: "nextRecurrenceTask.id",
      transform: (id: string) => (
        <RouterLink to={`/tasks/${parseGraphQLId(id)}`}>View Task</RouterLink>
      ),
    },
    {
      label: "Reason for Ban",
      path: "structuredMetadata.reasonForBan",
      transform: startCase,
    },
    {
      label: "Guest Urgency",
      path: "structuredMetadata.guestUrgency",
      transform: startCase,
    },
    {
      label: "SOP exception notes",
      path: "structuredMetadata.sopExceptionNotes",
      transform: identity,
    },
    {
      label: "Coffee packet",
      path: "structuredMetadata.requestedAmenities.coffeePacket",
      transform: identity,
    },
    {
      label: "Coffee filter",
      path: "structuredMetadata.requestedAmenities.coffeeFilter",
      transform: identity,
    },
    {
      label: "Coffee creamer",
      path: "structuredMetadata.requestedAmenities.coffeeCreamer",
      transform: identity,
    },
    {
      label: "Black tea (Earl Grey)",
      path: "structuredMetadata.requestedAmenities.teaBlack",
      transform: identity,
    },
    {
      label: "Green tea (Japanese Sencha)",
      path: "structuredMetadata.requestedAmenities.teaGreen",
      transform: identity,
    },
    {
      label: "Mint tea (Chamomile)",
      path: "structuredMetadata.requestedAmenities.teaMint",
      transform: identity,
    },
    {
      label: "Zing sugar",
      path: "structuredMetadata.requestedAmenities.sugarZing",
      transform: identity,
    },
    {
      label: "Regular sugar",
      path: "structuredMetadata.requestedAmenities.sugarRegular",
      transform: identity,
    },
    {
      label: "Shampoo",
      path: "structuredMetadata.requestedAmenities.shampoo",
      transform: identity,
    },
    {
      label: "Conditioner",
      path: "structuredMetadata.requestedAmenities.conditioner",
      transform: identity,
    },
    {
      label: "Bar soap",
      path: "structuredMetadata.requestedAmenities.barSoap",
      transform: identity,
    },
    {
      label: "Body lotion",
      path: "structuredMetadata.requestedAmenities.bodyLotion",
      transform: identity,
    },
    {
      label: "Facial towels",
      path: "structuredMetadata.requestedAmenities.towelFacial",
      transform: identity,
    },
    {
      label: "Hand towels",
      path: "structuredMetadata.requestedAmenities.towelHand",
      transform: identity,
    },
    {
      label: "Bath towels",
      path: "structuredMetadata.requestedAmenities.towelBath",
      transform: identity,
    },
    {
      label: "Bath mats",
      path: "structuredMetadata.requestedAmenities.bathMat",
      transform: identity,
    },
    {
      label: "Sheet set",
      path: "structuredMetadata.requestedAmenities.sheetSet",
      transform: identity,
    },
    {
      label: "Facial tissue",
      path: "structuredMetadata.requestedAmenities.facialTissue",
      transform: identity,
    },
    {
      label: "Toilet paper",
      path: "structuredMetadata.requestedAmenities.toiletPaper",
      transform: identity,
    },
    {
      label: "Olive oil (mini bottles)",
      path: "structuredMetadata.requestedAmenities.oliveOil",
      transform: identity,
    },
    {
      label: "Paper towels",
      path: "structuredMetadata.requestedAmenities.towelPaper",
      transform: identity,
    },
    {
      label: "Dishwasher pods",
      path: "structuredMetadata.requestedAmenities.dishwasherPod",
      transform: identity,
    },
    {
      label: "Large garbage bags",
      path: "structuredMetadata.requestedAmenities.garbageBagLarge",
      transform: identity,
    },
    {
      label: "Small garbage bags",
      path: "structuredMetadata.requestedAmenities.garbageBagSmall",
      transform: identity,
    },
    {
      label: "Laundry detergent pods",
      path: "structuredMetadata.requestedAmenities.laundryDetergentPod",
      transform: identity,
    },
  ],
};

export const MetadataCard = ({ task }: { task: TaskData }) => (
  <PanelPropertiesCard
    data={task}
    heading={METADATA_INFO.heading}
    options={METADATA_INFO.options}
  />
);
